import { LanguagesEnum } from '@/api/enums';
import { SystemRoles } from '@/client/users';
import { LanguageContext } from '@/common/context/LanguageContext';
import { SecurityProfileOnboarding } from '@/components/security-profile/onboarding/SecurityProfileOnboarding';
import { useSecurityProfileQuiz } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { LoadingPage } from '@/pages';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { branchAdminCheck } from '@/utils/helpers';
import { determineAttemptLanguage } from '@/utils/helpers/language.helper';
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

export const SecurityProfileOnboardingGuard: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { language } = useContext(LanguageContext);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const accountId = currentAccount?.id || '';
  const isAdmin =
    [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
      currentUser?.role.code as SystemRoles,
    ) && !branchAdminCheck(currentUser, currentAccount);
  const { quiz, isLoading } = useSecurityProfileQuiz(accountId, {
    enabled: !!accountId && isAdmin && !isDismissed,
  });

  const quizRef = useRef(quiz);
  if (quiz) quizRef.current = quiz;

  const isFirstLogin = useMemo(() => {
    const { firstLogin, lastLogin } = currentUser?.meta ?? {};

    if (!firstLogin && !lastLogin) return true;

    if (!firstLogin) return false;

    const firstLoginTime = new Date(firstLogin).getTime();
    const now = Date.now();
    const oneMinuteMs = 60 * 1000;

    return now - firstLoginTime <= oneMinuteMs;
  }, [currentUser?.meta?.firstLogin, currentUser?.meta?.lastLogin]);

  const shouldShowOnboarding = useMemo(() => {
    if (isDismissed) return false;
    if (isSubmitted) return true;
    if (currentAccount?.isSystem) return false;
    if (!isAdmin) return false;
    if (!quiz) return false;
    if (quiz.isCompleted) return false;

    return isFirstLogin || quiz.shouldShowOnboardingSurvey;
  }, [quiz, isAdmin, currentAccount, isFirstLogin, isDismissed, isSubmitted]);

  const attemptLanguage = useMemo(
    () =>
      determineAttemptLanguage(
        language,
        quiz?.supportedLanguages || [],
        null,
      ) as LanguagesEnum,
    [language, quiz?.supportedLanguages],
  );

  const handleSubmitted = useCallback(() => {
    setIsSubmitted(true);
  }, []);

  const handleComplete = useCallback(() => {
    setIsSubmitted(false);
    setIsDismissed(true);
  }, []);

  if (isLoading && isAdmin && !currentAccount?.isSystem) {
    return <LoadingPage message={t('user.loading')} />;
  }

  if (shouldShowOnboarding && quizRef.current) {
    return (
      <SecurityProfileOnboarding
        quiz={quizRef.current}
        accountId={accountId}
        attemptLanguage={attemptLanguage}
        userName={currentUser?.firstName}
        onSubmitted={handleSubmitted}
        onComplete={handleComplete}
      />
    );
  }

  return <Outlet />;
};
